import {
  getEntityCoords,
  getGeocodeCache,
  normalizeGeocodeQuery,
  setEntityCoords,
  setGeocodeCache,
} from './map-cache.js';

const LEGACY_CACHE_KEY = 'metis-geocode-v1';
const memoryCache = new Map();

function loadLegacyDiskCache() {
  try {
    const raw = localStorage.getItem(LEGACY_CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    for (const [key, value] of Object.entries(parsed)) {
      if (value?.lat != null) memoryCache.set(key, value);
    }
  } catch {
    /* ignore corrupt cache */
  }
}

loadLegacyDiskCache();

/** @returns {{ lat: number, lng: number } | null} */
export function parseCoordsFromMapsUrl(url) {
  if (!url) return null;
  const text = String(url);

  const atMatch = text.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (atMatch) {
    const lat = Number(atMatch[1]);
    const lng = Number(atMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const bangMatch = text.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);
  if (bangMatch) {
    const lat = Number(bangMatch[1]);
    const lng = Number(bangMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const llMatch = text.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
  if (llMatch) {
    const lat = Number(llMatch[1]);
    const lng = Number(llMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  const qMatch = text.match(/[?&](?:q|query)=(-?\d+\.?\d*)[,%20\s]+(-?\d+\.?\d*)/);
  if (qMatch) {
    const lat = Number(qMatch[1]);
    const lng = Number(qMatch[2]);
    if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  }

  return null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let lastGeocodeAt = 0;

function parsePlaceMeta(entity) {
  const raw = entity.place_meta ?? entity.placeMeta;
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Build ordered geocode query candidates for an entity. */
export function buildGeocodeQueries(entity, { country = '' } = {}) {
  const queries = [];
  const seen = new Set();

  const add = (q) => {
    const key = normalizeGeocodeQuery(q);
    if (!key || seen.has(key)) return;
    seen.add(key);
    queries.push(q.trim());
  };

  const name = entity.name?.trim();
  const meta = parsePlaceMeta(entity);
  const mapsUrl = entity.maps_url ?? entity.mapsUrl;

  if (meta?.displayName) add(meta.displayName);
  if (name && country) add(`${name}, ${country}`);
  if (name) add(name);
  if (mapsUrl) {
    const fromUrl = parseCoordsFromMapsUrl(mapsUrl);
    if (!fromUrl) {
      try {
        const u = new URL(mapsUrl);
        const q = u.searchParams.get('q') ?? u.searchParams.get('query');
        if (q) add(decodeURIComponent(q));
      } catch {
        /* ignore */
      }
    }
  }

  return queries;
}

/** Nominatim geocoding — rate-limited, cached (v2 + legacy). */
export async function geocodeQuery(query) {
  const q = String(query ?? '').trim();
  if (!q) return null;

  const cached = getGeocodeCache(q);
  if (cached.hit) {
    if (cached.failed) return null;
    return cached.coords;
  }

  const key = normalizeGeocodeQuery(q);
  if (memoryCache.has(key)) return memoryCache.get(key);

  const elapsed = Date.now() - lastGeocodeAt;
  if (elapsed < 1100) await sleep(1100 - elapsed);

  const params = new URLSearchParams({
    q,
    format: 'json',
    limit: '1',
  });

  const email = import.meta.env.VITE_NOMINATIM_EMAIL;
  if (email) params.set('email', email);

  lastGeocodeAt = Date.now();

  try {
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      setGeocodeCache(q, null, { failed: true });
      return null;
    }
    const rows = await res.json();
    const hit = rows?.[0];
    if (!hit?.lat || !hit?.lon) {
      setGeocodeCache(q, null, { failed: true });
      return null;
    }

    const coords = { lat: Number(hit.lat), lng: Number(hit.lon) };
    if (!Number.isFinite(coords.lat) || !Number.isFinite(coords.lng)) {
      setGeocodeCache(q, null, { failed: true });
      return null;
    }

    memoryCache.set(key, coords);
    setGeocodeCache(q, coords);
    return coords;
  } catch {
    return null;
  }
}

/** Sync resolution only (DB coords + Maps URL parse) — no network. */
export function resolveCoordinatesFast(entity, cacheOpts = null) {
  const lat = entity.lat ?? entity.latitude;
  const lng = entity.lng ?? entity.longitude;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat: Number(lat), lng: Number(lng), source: 'db' };
  }

  const fromUrl = parseCoordsFromMapsUrl(entity.maps_url ?? entity.mapsUrl);
  if (fromUrl) return { ...fromUrl, source: 'url' };

  if (cacheOpts?.instanceId && cacheOpts?.entityType && cacheOpts?.entityId) {
    const cached = getEntityCoords(
      cacheOpts.instanceId,
      cacheOpts.entityType,
      cacheOpts.entityId,
      cacheOpts.entityUpdatedAt ?? entity.updated_at ?? entity.updatedAt ?? null,
    );
    if (cached) return { ...cached, source: 'entityCache' };
  }

  return null;
}

/** Network geocoding with multi-query + entity/geocode cache writes. */
export async function resolveCoordinatesNetwork(
  entity,
  { country = '', instanceId = null, entityType = null, entityId = null, entityUpdatedAt = null } = {},
) {
  const fast = resolveCoordinatesFast(entity, { instanceId, entityType, entityId, entityUpdatedAt });
  if (fast) return fast;

  const queries = buildGeocodeQueries(entity, { country });
  if (!queries.length) {
    if (instanceId && entityType && entityId) {
      setEntityCoords(instanceId, entityType, entityId, null, {
        failed: true,
        entityUpdatedAt: entityUpdatedAt ?? entity.updated_at ?? entity.updatedAt ?? null,
      });
    }
    return null;
  }

  for (const query of queries) {
    const coords = await geocodeQuery(query);
    if (coords) {
      if (instanceId && entityType && entityId) {
        setEntityCoords(instanceId, entityType, entityId, coords, {
          source: 'geocode',
          entityUpdatedAt: entityUpdatedAt ?? entity.updated_at ?? entity.updatedAt ?? null,
        });
      }
      return { ...coords, source: 'network' };
    }
  }

  if (instanceId && entityType && entityId) {
    setEntityCoords(instanceId, entityType, entityId, null, {
      failed: true,
      entityUpdatedAt: entityUpdatedAt ?? entity.updated_at ?? entity.updatedAt ?? null,
    });
  }
  return null;
}

/** Resolve coordinates for a stop/base row (async, uses cache + network). */
export async function resolveCoordinates(entity, options = {}) {
  const result = await resolveCoordinatesNetwork(entity, options);
  if (!result) return null;
  return { lat: result.lat, lng: result.lng };
}

export function hasValidCoords(coords) {
  return coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng);
}

export function coordsOnly(result) {
  if (!hasValidCoords(result)) return null;
  return { lat: result.lat, lng: result.lng };
}
